import React, { useState, useEffect, useContext } from "react";
import { FiSun, FiMoon, FiMonitor, FiUser, FiTrash2, FiX, FiArrowLeft } from "react-icons/fi";
import { useTheme } from "../providers/ThemeContext";
import { AuthContext } from "../providers/auth-provider";
import axiosInstance from "../utils/axios-instance";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const SettingsPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { theme, updateTheme } = useTheme();

  const [name, setName] = useState(user?.fullName || "");
  const [initialName, setInitialName] = useState(user?.fullName || "");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });

  useEffect(() => {
    if (user?.fullName) { setName(user.fullName); setInitialName(user.fullName); }
  }, [user]);

  const handleThemeChange = (selectedTheme) => {
    updateTheme(selectedTheme);
    showNotification("Theme updated successfully!", "success");
  };

  const isSaveDisabled = () => name.trim() === initialName.trim() || name.trim() === "" || isSaving;

  const saveName = async () => {
    if (isSaveDisabled()) return;
    setIsSaving(true);
    try {
      const res = await axiosInstance.post('/user/changeusername', { id: user?.id, fullName: name });
      if (res.status === 200) { toast.success(res.data.message); setInitialName(name); }
      else toast.error(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update name");
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      const res = await axiosInstance.post('/chat/clearallconversations');
      if (res.status === 200) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete chats");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "" }), 2000);
  };

  const themeOptions = [
    { key: "light", icon: <FiSun className="text-2xl text-yellow-400" />, label: "Light" },
    { key: "dark", icon: <FiMoon className="text-2xl text-blue-400" />, label: "Dark" },
    { key: "system", icon: <FiMonitor className="text-2xl text-white/50" />, label: "System" },
  ];

  return (
    <div className="min-h-screen w-full bg-[#0f1829] py-10 px-4">
      <div className="w-full max-w-2xl mx-auto">

        {/* Page header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-white/40 hover:text-[#F05A22] transition-colors"
          >
            <FiArrowLeft className="text-base" />
            Back
          </button>
        </div>

        {/* Notification */}
        {notification.show && (
          <div className={`mb-5 p-4 rounded-xl text-sm font-medium ${
            notification.type === "success"
              ? "bg-green-500/10 text-green-400 border border-green-500/20"
              : "bg-red-500/10 text-red-400 border border-red-500/20"
          }`}>
            {notification.message}
          </div>
        )}

        {/* Card */}
        <div className="bg-[#162035] border border-white/10 rounded-2xl overflow-hidden divide-y divide-white/10">

          {/* Profile section */}
          <div className="p-7">
            <h2 className="flex items-center gap-2 text-base font-semibold text-white mb-5">
              <FiUser className="text-[#F05A22]" />
              User Profile
            </h2>
            <label className="block text-xs font-semibold text-white/40 mb-2 tracking-wide uppercase">Display Name</label>
            <div className="flex gap-0">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-grow px-4 py-2.5 rounded-l-xl border border-r-0 border-white/10 bg-[#1e2d4a] text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#F05A22]/50 focus:ring-1 focus:ring-[#F05A22]/20 transition-all"
                placeholder="Enter your name"
                disabled={isSaving}
              />
              <button
                onClick={saveName}
                disabled={isSaveDisabled()}
                className={`px-5 py-2.5 rounded-r-xl text-sm font-semibold text-white transition-all ${
                  isSaveDisabled()
                    ? "bg-white/10 text-white/30 cursor-not-allowed"
                    : "cursor-pointer"
                }`}
                style={!isSaveDisabled() ? { background: "linear-gradient(135deg, #F05A22, #E8407A)" } : {}}
              >
                {isSaving ? (
                  <div className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Saving
                  </div>
                ) : "Save"}
              </button>
            </div>
            <p className="text-xs text-white/25 mt-2">This name is displayed in your conversations.</p>
          </div>

          {/* Appearance section */}
          <div className="p-7">
            <h2 className="flex items-center gap-2 text-base font-semibold text-white mb-5">
              <FiMonitor className="text-[#7B4FBF]" />
              Appearance
            </h2>
            <div className="grid grid-cols-3 gap-3">
              {themeOptions.map(({ key, icon, label }) => (
                <div
                  key={key}
                  onClick={() => handleThemeChange(key)}
                  className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all text-center ${
                    theme === key
                      ? "border-[#F05A22] bg-[#F05A22]/10"
                      : "border-white/10 bg-[#1e2d4a] hover:border-white/20"
                  }`}
                >
                  <div className="flex justify-center mb-2">{icon}</div>
                  <p className="text-xs font-medium text-white/70">{label}</p>
                  {theme === key && (
                    <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#F05A22]" />
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-white/25 mt-3">Choose your preferred appearance. System default matches your device settings.</p>
          </div>

          {/* Danger zone */}
          <div className="p-7">
            <h2 className="flex items-center gap-2 text-base font-semibold text-red-400 mb-5">
              <FiTrash2 />
              Danger Zone
            </h2>
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-red-400">Delete All Chats</h3>
                  <p className="text-xs text-red-400/60 mt-1">This action is permanent and cannot be undone.</p>
                </div>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="px-5 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-semibold rounded-lg transition-all self-start sm:self-center whitespace-nowrap"
                >
                  Delete All
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#162035] border border-white/10 rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-bold text-white">Confirm Deletion</h3>
              <button onClick={() => setShowDeleteModal(false)} className="text-white/30 hover:text-white transition-colors">
                <FiX size={20} />
              </button>
            </div>
            <p className="text-sm text-white/50 mb-6">
              Are you sure you want to delete all chats? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                className="px-4 py-2 border border-white/10 text-white/60 rounded-lg hover:bg-white/10 hover:text-white text-sm transition-all"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-semibold transition-all flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Deleting...
                  </>
                ) : "Yes, Delete All"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;